import React, { useState, useRef, useCallback } from 'react';
import {
  ArrowLeft, Search, Plus, Minus, Trash2, ChevronDown, ChevronUp,
  ClipboardList, CheckCircle, MapPin, AlertTriangle
} from 'lucide-react';
import { directOrderService } from '../../../../services/directOrderService';
import { mediaAttachmentService } from '../../../../services/mediaAttachmentService';
import { SignatureCapture } from '../../../common/media/SignatureCapture';
import {
  CustomerDetailDto, DirectOrderItemDto, DirectOrderLineItem,
  ItemLocationDto, CreateDirectOrderRequest
} from '../../../../types/directOrder';

interface OrderBuilderViewProps {
  customer: CustomerDetailDto;
  onBack: () => void;
  onOrderCompleted: (message: string) => void;
  onPListCreated: (pListId: number) => void;
}

export const OrderBuilderView: React.FC<OrderBuilderViewProps> = ({
  customer,
  onBack,
  onOrderCompleted,
  onPListCreated
}) => {
  // Item search
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [itemResults, setItemResults] = useState<DirectOrderItemDto[]>([]);
  const [itemSearching, setItemSearching] = useState(false);
  const [itemLocations, setItemLocations] = useState<Record<string, ItemLocationDto[]>>({});
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const itemInputRef = useRef<HTMLInputElement>(null);

  // Shopping cart
  const [cartLines, setCartLines] = useState<DirectOrderLineItem[]>([]);

  // Collapsible sections
  const [showAddress, setShowAddress] = useState(false);
  const [showRefs, setShowRefs] = useState(false);

  // Address override
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');

  // References
  const [yourRef, setYourRef] = useState('');
  const [projectNo, setProjectNo] = useState('');
  const [customerOrderNo, setCustomerOrderNo] = useState('');

  // Action state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSignature, setShowSignature] = useState(false);

  // Item search with debounce
  const handleItemSearch = (term: string) => {
    setItemSearchTerm(term);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (term.length < 2) {
      setItemResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      try {
        setItemSearching(true);
        const items = await directOrderService.searchItems(term);
        setItemResults(items);
      } catch {
        setItemResults([]);
      } finally {
        setItemSearching(false);
      }
    }, 300);
  };

  // Load locations for an item
  const loadLocations = async (itemNo: string) => {
    if (itemLocations[itemNo]) return itemLocations[itemNo];
    try {
      const locs = await directOrderService.getItemLocations(itemNo);
      setItemLocations(prev => ({ ...prev, [itemNo]: locs }));
      return locs;
    } catch {
      return [];
    }
  };

  // Add item to cart
  const handleAddItem = async (item: DirectOrderItemDto) => {
    // Check if already in cart
    const existing = cartLines.find(l => l.itemNo === item.itemNo);
    if (existing) {
      setCartLines(prev => prev.map(l =>
        l.itemNo === item.itemNo ? { ...l, quantity: l.quantity + 1 } : l
      ));
    } else {
      // Get best location
      const locs = await loadLocations(item.itemNo);
      const bestLoc = locs.length > 0 ? locs[0] : null;

      setCartLines(prev => [...prev, {
        itemNo: item.itemNo,
        description1: item.description1,
        quantity: 1,
        binCode: bestLoc?.binCode || item.stdLocationName || null,
        locationName: bestLoc?.locationName || null
      }]);
    }

    // Clear search
    setItemSearchTerm('');
    setItemResults([]);
    itemInputRef.current?.focus();
  };

  // Cart operations
  const updateQuantity = (itemNo: string, delta: number) => {
    setCartLines(prev => prev.map(l => {
      if (l.itemNo !== itemNo) return l;
      const newQty = Math.max(1, l.quantity + delta);
      return { ...l, quantity: newQty };
    }));
  };

  const removeLine = (itemNo: string) => {
    setCartLines(prev => prev.filter(l => l.itemNo !== itemNo));
  };

  // Build request from state
  const buildRequest = useCallback((): CreateDirectOrderRequest => {
    return {
      customerNo: customer.customerNo,
      addressID: selectedAddressId || undefined,
      yourRef: yourRef || undefined,
      projectNo: projectNo || undefined,
      customerOrderNo: customerOrderNo || undefined,
      lines: cartLines.map(l => ({
        itemNo: l.itemNo,
        quantity: l.quantity,
        binCode: l.binCode || undefined,
        description1: l.description1 || undefined
      }))
    };
  }, [customer, selectedAddressId, yourRef, projectNo, customerOrderNo, cartLines]);

  // Handling A: Create order + PList
  const handleCreatePList = async () => {
    if (cartLines.length === 0) return;

    try {
      setIsSubmitting(true);
      setError(null);
      const result = await directOrderService.createOrderAndPick(buildRequest());
      onPListCreated(result.pListID);
    } catch (err: any) {
      setError(err.message || 'Kunne ikke opprette plukkliste');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handling B: Complete order directly - first show signature
  const handleCompleteOrder = () => {
    if (cartLines.length === 0) return;

    // Check all lines have BinCode
    const missingBin = cartLines.find(l => !l.binCode);
    if (missingBin) {
      setError(`Vare ${missingBin.itemNo} mangler lokasjon. Alle varer må ha lokasjon ved direkte fullføring.`);
      return;
    }

    setShowSignature(true);
  };

  // After signature captured, complete the order
  const handleSignatureConfirm = async (signatureDataUrl: string) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const result = await directOrderService.completeDirectOrder(buildRequest());

      // Save signature via MediaAttachment
      await mediaAttachmentService.addAttachment({
        entityType: 'Order',
        entityKey: result.orderNo.toString(),
        mediaType: 'signature',
        contentType: 'image/png',
        dataUrl: signatureDataUrl
      });

      onOrderCompleted(`Ordre ${result.orderNo} er fullført og lagerbeholdning er oppdatert.`);
    } catch (err: any) {
      setError(err.message || 'Kunne ikke fullføre ordre');
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalItems = cartLines.reduce((sum, l) => sum + l.quantity, 0);

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b bg-white">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 truncate">
            {customer.customerName} ({customer.customerNo})
          </div>
        </div>
      </div>

      {/* Item Search */}
      <div className="p-4 bg-white border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            ref={itemInputRef}
            type="text"
            value={itemSearchTerm}
            onChange={(e) => handleItemSearch(e.target.value)}
            placeholder="Søk vare / Skann strekkode..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
            autoFocus
          />
        </div>

        {/* Item search results dropdown */}
        {itemResults.length > 0 && (
          <div className="mt-2 border border-gray-200 rounded-lg bg-white shadow-lg max-h-60 overflow-y-auto">
            {itemResults.map(item => (
              <button
                key={item.itemNo}
                onClick={() => handleAddItem(item)}
                className="w-full text-left p-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-sm">{item.itemNo}</div>
                <div className="text-sm text-gray-600">{item.description1}</div>
                {item.stdLocationName && (
                  <div className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" /> {item.stdLocationName}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
        {itemSearching && (
          <div className="mt-2 text-center text-sm text-gray-500 py-2">Søker...</div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={() => setError(null)} className="text-xs text-red-600 underline mt-1">Lukk</button>
          </div>
        </div>
      )}

      {/* Shopping Cart */}
      <div className="flex-1 overflow-y-auto p-4">
        {cartLines.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Handlekurven er tom</p>
            <p className="text-sm mt-1">Søk eller skann varer for å legge til</p>
          </div>
        ) : (
          <>
            <div className="text-sm font-medium text-gray-500 mb-3">
              Handlekurv ({cartLines.length} varer, {totalItems} stk)
            </div>
            <div className="space-y-2">
              {cartLines.map(line => (
                <div key={line.itemNo} className="bg-white rounded-lg border border-gray-200 p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900">{line.itemNo}</div>
                      <div className="text-sm text-gray-600 truncate">{line.description1}</div>
                      {line.binCode && (
                        <div className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" /> {line.binCode}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => removeLine(line.itemNo)}
                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button
                      onClick={() => updateQuantity(line.itemNo, -1)}
                      className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-10 text-center font-medium">{line.quantity}</span>
                    <button
                      onClick={() => updateQuantity(line.itemNo, 1)}
                      className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Collapsible: Address */}
        <div className="mt-4">
          <button
            onClick={() => setShowAddress(!showAddress)}
            className="w-full flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50"
          >
            <span className="text-sm font-medium text-gray-700">Leveringsadresse</span>
            {showAddress ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showAddress && (
            <div className="mt-2 p-3 bg-white rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 mb-2">Standard: {customer.delAddress1}, {customer.delZipCode} {customer.delCity}</div>
              {customer.addresses.length > 0 && (
                <select
                  value={selectedAddressId}
                  onChange={(e) => setSelectedAddressId(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Bruk standardadresse</option>
                  {customer.addresses.map(addr => (
                    <option key={addr.addressID} value={addr.addressID}>
                      {addr.addressName || addr.addressID} - {addr.address1}, {addr.zipCode} {addr.city}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>

        {/* Collapsible: References */}
        <div className="mt-2">
          <button
            onClick={() => setShowRefs(!showRefs)}
            className="w-full flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50"
          >
            <span className="text-sm font-medium text-gray-700">Referanser</span>
            {showRefs ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showRefs && (
            <div className="mt-2 p-3 bg-white rounded-lg border border-gray-200 space-y-3">
              <div>
                <label className="text-xs text-gray-500">Din referanse</label>
                <input
                  type="text"
                  value={yourRef}
                  onChange={(e) => setYourRef(e.target.value)}
                  className="w-full mt-1 p-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Prosjektnr</label>
                <input
                  type="text"
                  value={projectNo}
                  onChange={(e) => setProjectNo(e.target.value)}
                  className="w-full mt-1 p-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Kundens ordrenr</label>
                <input
                  type="text"
                  value={customerOrderNo}
                  onChange={(e) => setCustomerOrderNo(e.target.value)}
                  className="w-full mt-1 p-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="p-4 bg-white border-t space-y-2">
        <button
          onClick={handleCreatePList}
          disabled={cartLines.length === 0 || isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          <ClipboardList className="w-5 h-5" />
          <div>
            <div>Opprett plukkliste</div>
            {cartLines.length > 0 && (
              <div className="text-xs font-normal opacity-80">Generer plukk for {cartLines.length} varer</div>
            )}
          </div>
        </button>
        <button
          onClick={handleCompleteOrder}
          disabled={cartLines.length === 0 || isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          <CheckCircle className="w-5 h-5" />
          <div>
            <div>Fullfør ordre (allerede plukket)</div>
            {cartLines.length > 0 && (
              <div className="text-xs font-normal opacity-80">Avslutter og oppdaterer lager</div>
            )}
          </div>
        </button>
      </div>

      {/* Signature Modal */}
      <SignatureCapture
        isOpen={showSignature}
        onConfirm={handleSignatureConfirm}
        onClose={() => setShowSignature(false)}
        title="Kundesignatur"
      />
    </div>
  );
};
